import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  downloadYearbookPDF,
  getYearbookLayout,
  saveYearbookLayout,
  YearbookSection,
} from '../../../endpoints/yearbook';
import LCSubPageLayout from './LCSubPageLayout';

function orderedSelection(sections: YearbookSection[]): string[] {
  return sections
    .filter((section) => section.selected)
    .slice()
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((section) => section.id);
}

const LCYearbookEditor: React.FC<{ chapterId: string; allowed: boolean }> = ({
  chapterId,
  allowed,
}) => {
  const { data, isLoading, error } = useApiGet(
    ['chapter-yearbook', chapterId],
    () => getYearbookLayout(chapterId),
    { enabled: allowed }
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [savedMessage, setSavedMessage] = useState('');
  const [downloadError, setDownloadError] = useState('');

  useEffect(() => {
    if (data?.sections) {
      setSelectedIds(orderedSelection(data.sections));
    }
  }, [data]);

  const byId = useMemo(() => {
    const map = new Map<string, YearbookSection>();
    data?.sections.forEach((section) => map.set(section.id, section));
    return map;
  }, [data]);

  const unselected = useMemo(() => {
    const chosen = new Set(selectedIds);
    return (data?.sections ?? [])
      .filter((section) => !chosen.has(section.id))
      .slice()
      .sort((a, b) => a.default_sort_order - b.default_sort_order);
  }, [data, selectedIds]);

  const { mutate: save, isPending: isSaving } = useApiSend(
    (ids: string[]) => saveYearbookLayout(chapterId, ids),
    () => setSavedMessage('Yearbook sections saved.'),
    () => setSavedMessage('')
  );

  const move = (index: number, direction: -1 | 1) => {
    const next = selectedIds.slice();
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    setSelectedIds(next);
    setSavedMessage('');
  };

  const toggle = (id: string, on: boolean) => {
    setSavedMessage('');
    setSelectedIds((current) =>
      on ? [...current, id] : current.filter((item) => item !== id)
    );
  };

  const download = async () => {
    setDownloadError('');
    try {
      const blob = await downloadYearbookPDF(chapterId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'chapter-yearbook.pdf';
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(
        err instanceof Error ? err.message : 'Could not download the yearbook.'
      );
    }
  };

  if (!allowed) {
    return (
      <Typography color="text.secondary">
        You do not have permission to export this chapter yearbook.
      </Typography>
    );
  }
  if (isLoading) {
    return <Typography color="text.secondary">Loading yearbook...</Typography>;
  }
  if (error || !data) {
    return (
      <Alert severity="error">Could not load the yearbook sections.</Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Membership year {data.membership_year}. The title page always prints
        first. Sections print in the order below. An empty section prints its
        heading and “No Content”.
      </Typography>
      {savedMessage && <Alert severity="success">{savedMessage}</Alert>}
      {downloadError && <Alert severity="error">{downloadError}</Alert>}
      <Typography variant="subtitle1" fontWeight={700}>
        Included
      </Typography>
      {selectedIds.length === 0 ? (
        <Typography color="text.secondary">
          No sections selected. The PDF will be the title page.
        </Typography>
      ) : (
        <List dense>
          {selectedIds.map((id, index) => {
            const section = byId.get(id);
            if (!section) return null;
            return (
              <ListItem
                key={id}
                secondaryAction={
                  <Box>
                    <IconButton
                      aria-label="Move up"
                      disabled={index === 0}
                      onClick={() => move(index, -1)}
                    >
                      <ArrowUpwardIcon />
                    </IconButton>
                    <IconButton
                      aria-label="Move down"
                      disabled={index === selectedIds.length - 1}
                      onClick={() => move(index, 1)}
                    >
                      <ArrowDownwardIcon />
                    </IconButton>
                  </Box>
                }
              >
                <Checkbox
                  checked
                  onChange={() => toggle(id, false)}
                  inputProps={{ 'aria-label': `Remove ${section.name}` }}
                />
                <ListItemText
                  primary={section.name}
                  secondary={section.description}
                />
              </ListItem>
            );
          })}
        </List>
      )}
      <Typography variant="subtitle1" fontWeight={700}>
        Available
      </Typography>
      <List dense>
        {unselected.map((section) => (
          <ListItem key={section.id}>
            <Checkbox
              checked={false}
              onChange={() => toggle(section.id, true)}
              inputProps={{ 'aria-label': `Add ${section.name}` }}
            />
            <ListItemText
              primary={section.name}
              secondary={section.description}
            />
          </ListItem>
        ))}
      </List>
      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          disabled={isSaving}
          onClick={() => save(selectedIds)}
        >
          Save order
        </Button>
        <Button variant="outlined" onClick={download}>
          Download PDF
        </Button>
      </Stack>
    </Stack>
  );
};

const LCYearbookPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return (
    <LCSubPageLayout
      title="Chapter Export"
      allow={(detail) => Boolean(detail.yearbook_access)}
    >
      {(detail) => (
        <LCYearbookEditor
          chapterId={id!}
          allowed={Boolean(detail.yearbook_access)}
        />
      )}
    </LCSubPageLayout>
  );
};

export default LCYearbookPage;
